/**
 * Phase 4f.C — Shop-Checkout-Erfolgsseite.
 * Pollt `/api/shop/checkout/status/{session_id}` bis ein finaler Zustand
 * erreicht ist (paid / failed / expired), leert dann den lokalen Warenkorb
 * und zeigt eine Bestätigung.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ShoppingBag } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function ShopCheckoutSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", attempt: 0 });
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "missing_session" });
            return;
        }
        let cancelled = false;
        let attempt = 0;

        const poll = async () => {
            if (cancelled) return;
            attempt += 1;
            setState((s) => ({ ...s, attempt }));
            try {
                const r = await axios.get(`${API}/api/shop/checkout/status/${sessionId}`);
                const d = r.data || {};
                if (d.status === "completed" || d.payment_status === "paid") {
                    setState({ status: "completed", data: d });
                    // Clear the cart only once, only for the slug we used.
                    if (!cleared.current) {
                        try {
                            const slug = sessionStorage.getItem("tw_shop_last_checkout_slug");
                            if (slug) {
                                localStorage.removeItem("tw_shop_cart_" + slug);
                                window.dispatchEvent(new CustomEvent("tw_shop_cart_update", { detail: { slug } }));
                            }
                        } catch { /* ignore */ }
                        cleared.current = true;
                    }
                    return;
                }
                if (d.status === "expired") {
                    setState({ status: "expired", data: d });
                    return;
                }
                if (attempt >= MAX_ATTEMPTS) {
                    setState({ status: "timeout", data: d });
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            } catch (e) {
                if (attempt >= MAX_ATTEMPTS) {
                    setState({ status: "error", error: e?.response?.data?.detail || e.message });
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const slug = (() => {
        try { return sessionStorage.getItem("tw_shop_last_checkout_slug") || ""; }
        catch { return ""; }
    })();
    const shopHref = slug ? `/shop/${slug}` : "/";

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="shop-checkout-success-card">
                {state.status === "polling" && (
                    <>
                        <Loader2 className="w-12 h-12 mx-auto text-[#FFCC00] animate-spin" />
                        <h1 className="font-display text-2xl font-black mt-4">Zahlung wird verarbeitet …</h1>
                        <p className="text-sm text-gray-600 mt-2" data-testid="shop-checkout-polling">
                            Bitte warten Sie kurz, wir bestätigen Ihre Zahlung bei Stripe (Versuch {state.attempt}/{MAX_ATTEMPTS}).
                        </p>
                    </>
                )}
                {state.status === "completed" && (
                    <>
                        <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
                        <h1 className="font-display text-2xl font-black mt-4" data-testid="shop-checkout-completed">Vielen Dank!</h1>
                        <p className="text-sm text-gray-700 mt-2">
                            Ihre Bestellung wurde erfolgreich bezahlt. Eine Bestätigung haben wir an die angegebene E-Mail-Adresse geschickt.
                        </p>
                        <div className="mt-4 text-xs font-mono-num text-gray-500">
                            Betrag: <strong>{(state.data?.amount_eur || 0).toFixed(2)} €</strong>
                            <br />
                            Session: {sessionId?.slice(0, 24)}…
                        </div>
                        <Link
                            to={shopHref}
                            className="inline-block mt-6 px-5 py-2.5 rounded-sm bg-[#FFCC00] hover:bg-[#FFD633] text-[#111827] font-bold uppercase tracking-wider text-xs"
                            data-testid="shop-checkout-back-to-shop"
                        >
                            <ShoppingBag className="w-4 h-4 inline mr-1.5" /> Zurück zum Shop
                        </Link>
                    </>
                )}
                {state.status === "expired" && (
                    <>
                        <AlertCircle className="w-12 h-12 mx-auto text-orange-500" />
                        <h1 className="font-display text-xl font-black mt-4">Sitzung abgelaufen</h1>
                        <p className="text-sm text-gray-600 mt-2">Die Zahlungs-Sitzung ist abgelaufen. Bitte starten Sie den Bestellvorgang erneut.</p>
                        <Link to={shopHref} className="inline-block mt-4 text-xs uppercase tracking-wider font-bold text-[#111827] hover:underline">Zurück zum Shop</Link>
                    </>
                )}
                {state.status === "timeout" && (
                    <>
                        <AlertCircle className="w-12 h-12 mx-auto text-amber-500" />
                        <h1 className="font-display text-xl font-black mt-4">Status noch nicht bestätigt</h1>
                        <p className="text-sm text-gray-600 mt-2">Wir konnten den finalen Zahlungsstatus noch nicht ermitteln. Sie erhalten eine E-Mail, sobald die Zahlung durchgegangen ist.</p>
                        <Link to={shopHref} className="inline-block mt-4 text-xs uppercase tracking-wider font-bold text-[#111827] hover:underline">Zurück zum Shop</Link>
                    </>
                )}
                {(state.status === "error" || state.status === "missing_session") && (
                    <>
                        <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
                        <h1 className="font-display text-xl font-black mt-4">Fehler</h1>
                        <p className="text-sm text-gray-600 mt-2">
                            {state.error || "Session-ID fehlt in der URL. Bitte kontaktieren Sie den Händler, falls der Betrag bereits abgebucht wurde."}
                        </p>
                        <Link to="/" className="inline-block mt-4 text-xs uppercase tracking-wider font-bold text-[#111827] hover:underline">Startseite</Link>
                    </>
                )}
            </div>
        </div>
    );
}
